import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'react-toastify';
import SEO from '../../components/common/SEO';
import LocationIndicator from '../../components/ui/LocationIndicator';
import { getUserCountryInfo } from '../../utils/geolocation';
import useExchangeRate from '../../hooks/useExchangeRate';
import useShippingFee from '../../hooks/useShippingFee';

const CURRENCY_SYMBOLS = {
  'Tunisia': 'TND',
  'default': '€'
};

const CartPage = () => {
  const navigate = useNavigate();
  const { items, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [userCountry, setUserCountry] = useState('default');
  const [isTunisia, setIsTunisia] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(true);
  const TND_EXCHANGE_RATE = useExchangeRate();
  const { fee: shipping, loading: shippingLoading } = useShippingFee(userCountry);

  useEffect(() => {
    const detectCountry = async () => {
      const info = await getUserCountryInfo();
      setUserCountry(info.country || 'default');
      setIsTunisia(info.isTunisia);
      setLoadingGeo(false);
    };
    detectCountry();
  }, []);

  const handleQuantityChange = (productId, variantIndex, newQuantity, stock) => {
    let clamped = Math.max(1, Math.min(newQuantity, stock));
    if (stock === 0 && clamped > 0) {
      navigate(`/notify-me?productId=${productId}`);
      return;
    }
    if (clamped < 1) {
      removeFromCart(productId, variantIndex);
      toast.info('Item removed from cart');
    } else {
      updateQuantity(productId, variantIndex, clamped);
    }
  };

  const handleRemoveItem = (productId, variantIndex, productName, weight) => {
    removeFromCart(productId, variantIndex);
    toast.info(`${productName} (${weight}) removed from cart`);
  };

  const handleClearCart = () => {
    clearCart();
    toast.info('Cart cleared');
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    navigate('/shop');
  };

  // Currency and shipping logic
  const subtotal = getCartTotal();
  const currency = isTunisia ? CURRENCY_SYMBOLS['Tunisia'] : CURRENCY_SYMBOLS['default'];
  const displaySubtotal = isTunisia ? (subtotal * TND_EXCHANGE_RATE) : subtotal;
  const displayTotal = isTunisia ? (subtotal * TND_EXCHANGE_RATE + (shipping || 0)) : (subtotal + (shipping || 0));

  const formatCurrency = (amount) => {
    if (isTunisia) {
      return `${amount.toFixed(2)} TND`;
    } else {
      return `€${amount.toFixed(2)}`;
    }
  };

  if (loadingGeo) {
    return <div>Detecting your location...</div>;
  }

  if (isTunisia && !TND_EXCHANGE_RATE) {
    return <div>Loading exchange rate...</div>;
  }

  if (shippingLoading) {
    return <div>Loading shipping fee...</div>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Shopping Cart"
        description="Review your Elixir honey selections in your shopping cart. Premium Mediterranean honey varieties ready for checkout."
        keywords="Elixir shopping cart, honey cart, Mediterranean honey checkout, premium honey purchase"
      />
      <Header />
      <main className="flex-grow flex flex-col">
        <div className="container mx-auto w-full">
          <h1 className="text-3xl font-bold mt-12 mb-8 px-4 lg:px-0">Shopping Cart</h1>
        </div>
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center min-h-[60vh]">
            <span className="text-center text-gray-500 text-lg mb-6">Your cart is empty.</span>
            <button
              onClick={handleContinueShopping}
              className="bg-[#D4AF37] text-white px-6 py-3 rounded-lg hover:bg-[#B8941F] transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 w-full">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {items.map((item) => (
                <div key={item.id + '-' + item.variantIndex} className="flex items-center bg-white shadow-md rounded-lg p-6 mb-6 max-w-2xl mx-auto">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-24 h-24 object-cover rounded mr-6 border"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <h2 className="text-lg font-bold text-gray-900">{item.title} <span className="text-xs font-normal text-gray-500 ml-2">({item.weight})</span></h2>
                      <span className="text-xl font-semibold text-yellow-700">{isTunisia ? formatCurrency(parseFloat(item.price) * TND_EXCHANGE_RATE * item.quantity) : formatCurrency(parseFloat(item.price) * item.quantity)}</span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3 max-w-xl line-clamp-3">{item.description}</p>
                    {/* <span className={`text-xs font-semibold ${item.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>In stock: {item.stock}</span> */}
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => handleQuantityChange(item.id, item.variantIndex, item.quantity - 1, item.stock)} className="px-2 py-1 bg-gray-200 rounded">-</button>
                      <input
                        type="number"
                        min={1}
                        max={item.stock}
                        value={Math.min(item.quantity, item.stock)}
                        onChange={e => {
                          let val = parseInt(e.target.value, 10);
                          if (isNaN(val) || val < 1) val = 1;
                          if (val > item.stock) val = item.stock;
                          handleQuantityChange(item.id, item.variantIndex, val, item.stock);
                        }}
                        className="w-12 text-center border rounded"
                      />
                      <button 
                        onClick={() => handleQuantityChange(item.id, item.variantIndex, item.quantity + 1, item.stock)} 
                        className="px-2 py-1 bg-gray-200 rounded"
                        disabled={item.quantity >= item.stock}
                        style={{ opacity: item.quantity >= item.stock ? 0.5 : 1, cursor: item.quantity >= item.stock ? 'not-allowed' : 'pointer' }}
                      >
                        +
                      </button>
                      <button 
                        onClick={() => handleRemoveItem(item.id, item.variantIndex, item.title, item.weight)}
                        className="ml-4 text-red-500 hover:underline text-sm"
                      >
                        Remove
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{isTunisia ? formatCurrency(parseFloat(item.price) * TND_EXCHANGE_RATE) : formatCurrency(parseFloat(item.price))} each</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary - only show if cart has items */}
            <div className="bg-white shadow-md border border-gray-200 rounded-lg p-6 h-fit">
              <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
              {/* Location Indicator */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <LocationIndicator />
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-700">Subtotal</span>
                  <span className="font-medium">{formatCurrency(displaySubtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Shipping</span>
                  <span className="text-sm text-gray-500">{formatCurrency(shipping || 0)}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>{formatCurrency(displayTotal)}</span>
                </div>
              </div>
              <button 
                onClick={handleCheckout}
                className="w-full mt-4 bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-3 rounded transition"
              >
                Proceed to Checkout
              </button>
              <button
                onClick={handleContinueShopping}
                className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg mt-3 hover:bg-gray-300 transition-colors"
              >
                Continue Shopping
              </button>
              <button
                onClick={handleClearCart}
                className="w-full bg-red-100 text-red-600 py-2 rounded-lg mt-3 hover:bg-red-200 transition-colors"
              >
                Clear Cart
              </button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
